import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { DialogResult, DialogService, DspxCardSetBaseFormComponent, FormNavigationService, MessagePlaceholders, SpinnerService } from 'dspx-core';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { FILTER_ACTIONS, FILTER_QUESTION_CODES, FORM_ACTIONS, FORM_CUSTOM_ACTIONS, NAV_TAB_NUMBERS, noFilterOptionCode, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { ErrorService } from '../../services/error.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { BaseCardSetListComponent } from "../base-card-set-list-component/base-card-set-list.component";
import { GbhFormGridFilterRequest } from '../../interfaces/gbh-form-grid-filter-request.interface';

@Component({
  selector: 'app-gbh-form-list',
  templateUrl: './gbh-form-list.component.html',
  styleUrl: './gbh-form-list.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class GbhFormListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  activeDialog: string;

  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private errorService: ErrorService,
    private excelExportService: ExcelExportService,
    private formNavigationService: FormNavigationService) {

    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.gbhFormList,
      () => API_ENDPOINTS.dashboard.getGbhForms,
      true);
  }

  ngOnInit(): void {
    this.route.url.subscribe((url) => {
      if (url.length === 0) {
        // In the case where this component is being loaded as part of the default (empty) route, we want to force it
        // to the GBH Form List route so that the menu will highlight the selected option.
        this.router.navigate(['/', ROUTE_CONSTANTS.gbhForm.list]);
      }
    });

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
  }

  formLoad(event: any): void {
    this.loadGridDataOnFormLoad(this.getDefaultFilterRequest());
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.gbhFormListForm.gbhFormsFilter: {
        switch (event.action) {
          case FILTER_ACTIONS.common.applyFilters: {
            this.loadGridData(this.getFilterRequest());
            break;
          }
          case FILTER_ACTIONS.common.clearFilters: {
            this.loadGridData(this.getDefaultFilterRequest());
            break;
          }
        }
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.gbhFormListForm.gbhFormsPlaceholder: {
        switch (event.event.type) {
          case FORM_ACTIONS.gbhFormListForm.view: {
            const routePrefix = event.event.data.isApproverFormAccessible
              ? ROUTE_CONSTANTS.approverGbhForm.viewPrefix
              : ROUTE_CONSTANTS.gbhForm.viewPrefix;
            this.router.navigate(['/', routePrefix, event.event.data.id]);
            break;
          }
          case FORM_ACTIONS.gbhFormListForm.edit:
          case FORM_ACTIONS.gbhFormListForm.colLink: {
            const routePrefix = event.event.data.isApproverFormAccessible
              ? ROUTE_CONSTANTS.approverGbhForm.prefix
              : ROUTE_CONSTANTS.gbhForm.prefix;
            this.router.navigate(['/', routePrefix, event.event.data.id]);
            break;
          }
          case FORM_ACTIONS.gbhFormListForm.delete: {
            this.deleteGbhForm(event.event.data);
            break;
          }
          case FORM_ACTIONS.gbhFormListForm.custom: {
            switch (event.event.data.buttonType) {
              case FORM_CUSTOM_ACTIONS.gbhFormListForm.customAdd: {
                this.router.navigate(['/', ROUTE_CONSTANTS.gbhForm.prefix]);
                break;
              }
              case FORM_CUSTOM_ACTIONS.gbhFormListForm.bulkExport: {
                this.excelExportService.exportGbhFormsToXlsx({});
                break;
              }
            }
            break;
          }
        }
        break;
      }
    }
  }

  private getDefaultFilterRequest(): GbhFormGridFilterRequest {
    return {
      statusId: noFilterOptionCode
    };
  }

  private getFilterRequest(): GbhFormGridFilterRequest {

    const filterQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.gbhFormListForm.gbhForms,
      PAGE_NUMBERS.gbhFormListForm.gbhForms,
      SECTION_CODES.gbhFormListForm.resultSection,
      QUESTION_CODES.gbhFormListForm.gbhFormsFilter];

    const filterQuestionModel = this.getQuestionValue(this.model, ...filterQuestionPath);

    const filterRequest: GbhFormGridFilterRequest = {
      gbhId: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.gbhId],
      submissionDateFrom: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.submissionDateFrom],
      submissionDateTo: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.submissionDateTo],      
      declarerId: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.declarerId],
      approverId: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.approverId],
      offerorOrganisation: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.offerorOrganisation],
      statusId: filterQuestionModel?.[FILTER_QUESTION_CODES.gbhFormListForm.statusId]
    };

    return filterRequest;
  }

  private deleteGbhForm(gbhForm: any): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.gbhFormList.deleteConfirmation, { gbhId: gbhForm.gbhId })
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.delete(`${API_ENDPOINTS.gbhForm.delete}/${gbhForm.id}`)
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();

                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.deleteGbhForm.includes(data.message);
                    this.activeDialog = isSuccess ? POP_UP_CODES.gbhFormList.deleteSuccessful : data.message;

                    const messagePlaceholders: MessagePlaceholders = { gbhId: gbhForm.gbhId };
                    this.dialogService.showDialog(this.activeDialog, null, messagePlaceholders);

                    if (isSuccess) {
                      this.loadGridData(this.getFilterRequest());
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }
}
